import { useMemo } from "react";
import { enabledValuesFor } from "../lib/variantAxes";

/**
 * VariantAxisPicker — axis-based variant selector used by both the
 * PDP inline picker and the ``ProductVariantPicker`` bottom-sheet.
 *
 * Renders each axis as its own row of chips (Color, Size, …). Chips
 * that would form an invalid combination given the current selection
 * on other axes are disabled + struck-through so only valid combos
 * can be picked.
 *
 * iter120 — Per-chip prices intentionally removed for a cleaner UI.
 * The main product price at the top of the PDP (and the "Selected"
 * strip inside the bottom-sheet) updates to reflect the currently
 * picked variant, so users only see a single price at a time.
 *
 * Props:
 *  • axes         — output of ``buildAxes(product.variants)``
 *  • variants     — the raw product.variants list (used for validity)
 *  • selection    — {axisKey: value}
 *  • onChange     — (axisKey, value) callback
 *  • dense        — smaller chips for the listing card picker.
 */
export default function VariantAxisPicker({
    axes, variants, selection, onChange, dense = false,
}) {
    if (!axes?.length) return null;
    return (
        <div className={dense ? "space-y-2" : "space-y-4"} data-testid="variant-axis-picker">
            {axes.map((axis) => (
                <AxisRow
                    key={axis.key}
                    axis={axis}
                    variants={variants}
                    selection={selection}
                    onChange={onChange}
                    dense={dense}
                />
            ))}
        </div>
    );
}

function AxisRow({ axis, variants, selection, onChange, dense }) {
    const enabledSet = useMemo(
        () => enabledValuesFor(variants, axis.key, selection),
        [variants, axis.key, selection],
    );
    const selectedValue = selection?.[axis.key];
    const isColor = axis.key === "COLOR" && axis.values.some((o) => o.color_hex);
    const chipCls = dense ? "px-2 py-1 text-xs" : "px-3 py-2 text-sm";

    return (
        <div data-testid={`variant-axis-${axis.key}`}>
            <p className={`uppercase tracking-wider text-muted-foreground font-semibold mb-2 ${dense ? "text-[10px]" : "text-xs"}`}>
                {axis.label}
                {selectedValue && !isColor && (
                    <span className="ml-2 text-foreground normal-case tracking-normal font-bold">
                        {axis.values.find((o) => o.value === selectedValue)?.label}
                    </span>
                )}
            </p>
            <div className="flex flex-wrap gap-2">
                {axis.values.map((opt) => {
                    const on = selectedValue === opt.value;
                    const available = enabledSet.has(opt.value);
                    // Chips are ALWAYS clickable — Amazon/Flipkart pattern:
                    // tapping an "incompatible with current selection" chip
                    // is allowed and the parent auto-resolves other axes.
                    // Only unavailable-in-catalogue chips would be disabled,
                    // but since we filtered ``enabled !== false`` at input
                    // there are none — every chip is clickable.
                    if (isColor && opt.color_hex) {
                        return (
                            <button
                                key={opt.value}
                                data-testid={`variant-chip-${axis.key}-${opt.value}`}
                                aria-label={opt.label}
                                title={opt.label}
                                onClick={() => onChange(axis.key, opt.value)}
                                className={`w-8 h-8 rounded-full border-2 transition ${on ? "border-primary ring-2 ring-primary/40" : "border-border"} ${!available ? "opacity-50" : ""}`}
                                style={{ backgroundColor: opt.color_hex }}
                            />
                        );
                    }
                    return (
                        <button
                            key={opt.value}
                            data-testid={`variant-chip-${axis.key}-${opt.value}`}
                            onClick={() => onChange(axis.key, opt.value)}
                            className={`${chipCls} rounded-xl border-2 font-semibold inline-flex items-center gap-1.5 transition ${on ? "border-primary bg-primary/10 text-primary" : "border-border text-foreground"} ${!available ? "opacity-60" : ""}`}
                        >
                            <span className={!available ? "line-through" : ""}>{opt.label}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
